export interface ScreenPoint {
  x: number;
  y: number;
}

export interface DrawLabelPlacementInput {
  start: ScreenPoint;
  end: ScreenPoint;
  /** Points occupied by visible vertex or midpoint handles. */
  handlePoints: ScreenPoint[];
  /** Previously placed labels in this same rendering pass. */
  labelPoints?: ScreenPoint[];
  viewport: { width: number; height: number };
}

// Keep drawing distance text visually associated with its segment. The handle
// itself is rendered beneath passive labels, so a compact 10–12px center offset
// protects readability without making the label look detached from the edge.
export const DRAW_LABEL_HANDLE_CLEARANCE_PX = 10;
export const DRAW_LABEL_MAX_OFFSET_PX = 12;
export const DRAW_LABEL_EDGE_PADDING_PX = 10;

function squaredDistance(a: ScreenPoint, b: ScreenPoint): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/**
 * Choose a stable screen position for a drawing distance label. The placement
 * is intentionally presentation-only: it never changes map geometry.
 */
export function resolveDrawLabelPlacement(input: DrawLabelPlacementInput): ScreenPoint {
  const { start, end, handlePoints, labelPoints = [], viewport } = input;
  const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length < 1) return midpoint;

  const normal = { x: -dy / length, y: dx / length };
  const candidates = [
    DRAW_LABEL_HANDLE_CLEARANCE_PX,
    -DRAW_LABEL_HANDLE_CLEARANCE_PX,
    DRAW_LABEL_MAX_OFFSET_PX,
    -DRAW_LABEL_MAX_OFFSET_PX,
  ].map((offset) => ({
    x: midpoint.x + normal.x * offset,
    y: midpoint.y + normal.y * offset,
  }));

  const minX = DRAW_LABEL_EDGE_PADDING_PX;
  const minY = DRAW_LABEL_EDGE_PADDING_PX;
  const maxX = Math.max(minX, viewport.width - DRAW_LABEL_EDGE_PADDING_PX);
  const maxY = Math.max(minY, viewport.height - DRAW_LABEL_EDGE_PADDING_PX);

  return candidates
    .map((candidate) => ({
      x: Math.min(maxX, Math.max(minX, candidate.x)),
      y: Math.min(maxY, Math.max(minY, candidate.y)),
    }))
    .reduce((best, candidate) => {
      const score = Math.min(
        ...handlePoints.map((point) => squaredDistance(candidate, point)),
        ...labelPoints.map((point) => squaredDistance(candidate, point)),
      );
      const bestScore = Math.min(
        ...handlePoints.map((point) => squaredDistance(best, point)),
        ...labelPoints.map((point) => squaredDistance(best, point)),
      );
      return score > bestScore ? candidate : best;
    }, candidates[0]);
}
